import './index.css';
import { useEffect, useState } from 'react';
import { usePrefersReducedMotion } from 'hooks';

/**
 * EnterTransition — sapphire-toned luxe intro shown once on first load.
 *
 * A deep panel fills the viewport. Its base is a deep blue-ink, and a separate
 * wash layer paints a rich navy→sapphire gradient with a soft luminous glow
 * concentrated behind the name — so the cover reads clearly blue/sapphire
 * (foreshadowing the site's blue globe), still deep and premium. The name
 * "Sébastien Friedberg" is revealed line-by-line via an overflow-hidden mask
 * slide-up (each line rises from below its mask, staggered, in a cool-tinted
 * off-white). One thin sapphire→cyan hairline draws in beneath the name as the
 * glowing accent.
 *
 * The arrival is the point of care: rather than cutting from a dark/blue cover
 * to the light "paper" site, the exit first dissolves name/hairline/label,
 * then — crucially — fades the blue *wash layer* out while the base panel's
 * *background-color* warms from deep blue-ink up toward the exact paper tone
 * (rgb(244 242 236)). Because the blue lives on its own opacity-driven layer
 * (a solid background-color can't transition to a gradient), the blue can melt
 * away exactly as the base warms to paper. Only once the panel is paper-toned
 * does it fade out. The eye never sees a hard dark/blue→light jump; the intro
 * resolves continuously into the identical paper hero behind it.
 *
 * Sequence (normal motion):
 *   0ms      'enter'  blue cover present (base + navy→sapphire wash + glow);
 *                     lines slide up out of their masks (staggered,
 *                     ease-out-expo); hairline draws in; mono label fades last
 *   ~2900ms  'exit'   name/hairline/label dissolve; the blue wash fades out
 *                     while the base background lifts blue-ink→paper
 *                     (~900ms, soft ease-in-out); the paper-toned panel then
 *                     fades gently to reveal the identical hero.
 *                     pointer-events:none the instant exit begins (failsafe)
 *   ~4700ms  unmount  (timer covers the full, longer exit so it never cuts off)
 *
 * Respects prefers-reduced-motion (never shown). Always aria-hidden; never
 * traps focus; SSR/react-snap safe (client-only effect).
 */
const EXIT_DELAY = 2900;
const UNMOUNT_DELAY = 4700;

const EnterTransition = () => {
  const prefersReducedMotion = usePrefersReducedMotion();
  const [phase, setPhase] = useState('enter');
  const [mounted, setMounted] = useState(true);

  useEffect(() => {
    if (prefersReducedMotion) {
      setMounted(false);
      return;
    }

    const exitTimer = setTimeout(() => setPhase('exit'), EXIT_DELAY);
    const unmountTimer = setTimeout(() => setMounted(false), UNMOUNT_DELAY);

    return () => {
      clearTimeout(exitTimer);
      clearTimeout(unmountTimer);
    };
  }, [prefersReducedMotion]);

  if (!mounted) return null;

  const isExiting = phase === 'exit';

  return (
    <div
      className={`enter-transition enter-transition--${phase}`}
      aria-hidden="true"
      // Failsafe: never block clicks once the panel starts resolving away
      style={isExiting ? { pointerEvents: 'none' } : undefined}
    >
      <div className="enter-transition__panel">
        {/* Blue character lives on its own layer so it can fade out on exit
            while the base panel warms blue-ink → paper underneath it. */}
        <div className="enter-transition__wash" />
        <div className="enter-transition__mark">
          <span className="enter-transition__line-mask">
            <span className="enter-transition__word enter-transition__word--1">
              Sébastien
            </span>
          </span>
          <span className="enter-transition__line-mask">
            <span className="enter-transition__word enter-transition__word--2">
              Friedberg
            </span>
          </span>
          <span className="enter-transition__hairline" />
          <span className="enter-transition__label">
            Full-Stack Engineer · Paris
          </span>
        </div>
      </div>
    </div>
  );
};

export default EnterTransition;
